import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { DateTime, Settings } from "luxon";
import { CookieService } from "ngx-cookie-service";
import { UtilsService } from "src/app/shared/services/utils.service";
import { Timeframe } from "src/app/shared/types/hydrao-types";
import { Filters } from "../models/filters.model";
import { AuthStore } from "./auth.store";
import { PlacesStore } from "./places.store";
import { UserStore } from "./user.store";

export interface FiltersQueryParams {
  date: string;
  tf: string;
  place: number;
  panel: "opened" | "closed";
}

@Injectable({
  providedIn: "root",
})
export class FiltersStore {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private utilsService: UtilsService = inject(UtilsService);
  private cookieService: CookieService = inject(CookieService);
  private authStore: AuthStore = inject(AuthStore);
  private placesStore: PlacesStore = inject(PlacesStore);
  private userStore: UserStore = inject(UserStore);
  private router: Router = inject(Router);

  // Define signals for the store state
  private readonly filterPanelOpened = signal<boolean>(false);
  private readonly filters = signal<Filters>(
    new Filters(DateTime.local().toUTC(), Timeframe.Month, null)
  );

  private readonly canSelectDay = signal<boolean>(false);
  private readonly canSelectHour = signal<boolean>(false);
  private readonly canSelectChildPlace = signal<boolean>(true);

  // Getter signals for external access
  readonly panelOpened$ = this.filterPanelOpened.asReadonly();
  readonly filters$ = this.filters.asReadonly();
  readonly placeId$ = computed(() => {
    const filters = this.filters();
    const placesById = this.placesStore.placesWithAllByIds$();
    const hasPlaces = this.placesStore.hasPlaces$();

    const placesIds = placesById ? Object.keys(placesById) : [];

    // no filter on unknown place
    if (
      !hasPlaces ||
      filters.placeId === null ||
      (filters.placeId !== -1 && placesIds.indexOf(filters.placeId + "") < 0)
    )
      return null;

    return filters.placeId;
  });
  readonly canSelectDay$ = this.canSelectDay.asReadonly();
  readonly canSelectHour$ = this.canSelectHour.asReadonly();
  readonly canSelectChildPlace$ = this.canSelectChildPlace.asReadonly();
  readonly placeLabel$ = computed(() => {
    const placeId = this.placeId$();

    if (placeId > 0) {
      return this.placesStore.getLabel(placeId);
    } else if (placeId === -1) {
      return $localize`without place`;
    } else {
      return $localize`all places`;
    }
  });
  readonly placeShortLabel$ = computed(() => {
    const hasPlaces = this.placesStore.hasPlaces$();
    const placeId = this.placeId$();
    const placesById = this.placesStore.placesById$();

    if (!hasPlaces) {
      return $localize`create`;
    }

    if (placeId > 0) {
      return placesById[placeId]?.label;
    } else if (placeId === -1) {
      return $localize`without`;
    } else {
      return $localize`all`;
    }
  });
  readonly timeframe$ = computed(() => this.filters().timeframe);
  readonly date$ = computed(() => {
    const user = this.userStore.user$();
    const filters = this.filters$();

    return user ? filters.date.setZone(user.timezone) : filters.date;
  });
  readonly dateFrom$ = computed(() =>
    this.utilsService.getStartDateForTimeFrame(
      this.date$(),
      this.filters().timeframe
    )
  );
  readonly dateTo$ = computed(() =>
    this.utilsService.getEndDateForTimeFrame(
      this.date$(),
      this.filters().timeframe
    )
  );
  readonly dateLabel$ = computed(() => {
    const date = this.date$();
    const timeFrame = this.filters().timeframe;

    const locale = $localize.locale || "en";

    const formatDate = (d: DateTime, options: Intl.DateTimeFormatOptions) => {
      return new Intl.DateTimeFormat(locale, options).format(d.toJSDate());
    };

    switch (timeFrame) {
      case Timeframe.Hour:
        return (
          $localize`${formatDate(date, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone: Settings.defaultZone.name,
          })}:date:` +
          "\n" +
          $localize`at ${formatDate(date, {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: Settings.defaultZone.name,
          })}:hour:`
        );

      case Timeframe.Day:
        return $localize`${formatDate(date, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          timeZone: Settings.defaultZone.name,
        })}:date:`;

      case Timeframe.Week:
        const dateBegin = date.startOf("week");
        const dateEnd = date.endOf("week");
        return (
          $localize`${formatDate(dateBegin, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone: Settings.defaultZone.name,
          })}:date:` +
          " -\n " +
          $localize`${formatDate(dateEnd, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            timeZone: Settings.defaultZone.name,
          })}:date:`
        );

      case Timeframe.Month:
        return $localize`${formatDate(date, {
          month: "short",
          year: "numeric",
          timeZone: Settings.defaultZone.name,
        })}:date:`;

      case Timeframe.Year:
        return $localize`${formatDate(date, {
          year: "numeric",
          timeZone: Settings.defaultZone.name,
        })}:date:`;
    }
  });
  readonly graphGrouping$ = computed(() =>
    this.utilsService.getGraphGroupingFromTimeFrame(this.filters().timeframe)
  );

  readonly nextDate$ = computed(() => {
    const date = this.date$();
    const timeFrame = this.timeframe$();

    // compute next date
    let nextDate = null;
    switch (timeFrame) {
      case Timeframe.Hour:
        nextDate = date.plus({ hours: 1 });
        break;
      case Timeframe.Day:
        nextDate = date.plus({ days: 1 });
        break;
      case Timeframe.Week:
        nextDate = date.plus({ days: 7 });
        break;
      case Timeframe.Month:
        nextDate = date.plus({ months: 1 });
        break;
      case Timeframe.Year:
        nextDate = date.plus({ years: 1 });
        break;
    }

    // check if next date not exceed current date
    let currentDate = this._getRoundedDate(DateTime.local());

    return nextDate <= currentDate ? nextDate : null;
  });

  readonly previousDate$ = computed(() => {
    const date = this.date$();
    const timeFrame = this.timeframe$();

    // compute previous date
    let previousDate = null;
    switch (timeFrame) {
      case Timeframe.Hour:
        previousDate = date.minus({ hours: 1 });
        break;
      case Timeframe.Day:
        previousDate = date.minus({ days: 1 });
        break;
      case Timeframe.Week:
        previousDate = date.minus({ days: 7 });
        break;
      case Timeframe.Month:
        previousDate = date.minus({ months: 1 });
        break;
      case Timeframe.Year:
        previousDate = date.minus({ years: 1 });
        break;
    }

    return previousDate;
  });

  constructor() {
    effect(
      () => {
        const user = this.userStore.user$();

        if (!user || !user.timezone) return;

        //to force refresh date on user timezone changes
      },
      { allowSignalWrites: true }
    );

    this.route.queryParams.subscribe(async (param) => {
      let newFilters = this.filters().clone();

      if (!param.date && !param.tf && !param.place) {
        // add to url query params with current values
        // const filters = this.filters().clone();
        // this.updateFiltersInUrl(filters, this.filterPanelOpened());
      } else {
        // update filters from url params
        if (param.date) {
          console.log(
            "url param date = ",
            param.date,
            DateTime.fromISO(param.date)
          );
          newFilters.date = DateTime.fromISO(param.date);
        } /*else {
          newFilters.date = this.date$()
            ? this.date$()
            : DateTime.local().toUTC();
        }*/

        if (param.tf) {
          newFilters.timeframe = param.tf;
        } /* else {
          newFilters.timeframe = Timeframe.Month;
        } */

        if (!this.userStore.canViewPlaces$()) {
          newFilters.placeId = null;
        } else {
          if (param.place && !isNaN(+param.place)) {
            newFilters.placeId = parseInt(param.place);
          } else {
            newFilters.placeId = null;
          }
        }

        console.log("ADAPT FILTERS FROM URL", newFilters);
        this.updateFilters(newFilters);

        // update panel state from url param
        let panelOpened = param.panel == "opened";
        this.updatedPanelState(panelOpened);
      }
    });
  }

  togglePanelState(): void {
    const opened = !this.filterPanelOpened();
    this.updatedPanelState(opened);
  }

  updatedPanelState(opened: boolean): void {
    if (this.filterPanelOpened() !== opened) {
      this.filterPanelOpened.set(opened);
      if (opened) {
        this.cookieService.set("date_selector_pulse_ack", "ok");
      }
    }
  }

  updateCanSelectChildPlace(enabled: boolean) {
    if (this.canSelectChildPlace() !== enabled) {
      this.canSelectChildPlace.set(enabled);
    }
  }

  updateCanSelectDay(enabled: boolean) {
    if (this.canSelectDay() !== enabled) {
      this.canSelectDay.set(enabled);
    }
  }

  updateCanSelectHour(enabled: boolean) {
    if (this.canSelectHour() !== enabled) {
      this.canSelectHour.set(enabled);
    }
  }

  async zoomOut() {
    let newTimeframe;
    let newDate: DateTime = this.date$();
    switch (this.timeframe$()) {
      case Timeframe.Month:
        newTimeframe = Timeframe.Year;
        newDate = newDate.set({ hour: 0, day: 1 });
        break;
      case Timeframe.Week:
      case Timeframe.Day:
        newTimeframe = Timeframe.Month;
        newDate = newDate.set({ hour: 0 });
        break;
      case Timeframe.Hour:
        newTimeframe = Timeframe.Day;
        break;
    }

    const cloneFilters = this.filters().clone();
    cloneFilters.date = newDate;
    cloneFilters.timeframe = newTimeframe;

    await this.updateFiltersInUrl(cloneFilters);
  }

  async zoomIn(newDate: DateTime) {
    let newTimeframe;
    let timeframe = this.timeframe$();

    if (timeframe === Timeframe.Hour) return;

    switch (timeframe) {
      case Timeframe.Year:
        newTimeframe = Timeframe.Month;
        break;
      case Timeframe.Month:
        newTimeframe = Timeframe.Day;
        break;
      case Timeframe.Week:
        newTimeframe = Timeframe.Day;
        break;
      case Timeframe.Day:
        newTimeframe = Timeframe.Hour;
        break;
    }

    const cloneFilters = this.filters().clone();
    cloneFilters.date = newDate;
    cloneFilters.timeframe = newTimeframe;

    await this.updateFiltersInUrl(cloneFilters);
  }

  async next() {
    const newDate = this.nextDate$();

    if (newDate) {
      const cloneFilters = this.filters().clone();
      cloneFilters.date = newDate;
      await this.updateFiltersInUrl(cloneFilters);
    }
  }

  async previous() {
    const newDate = this.previousDate$();

    if (newDate) {
      const cloneFilters = this.filters().clone();
      cloneFilters.date = newDate;
      await this.updateFiltersInUrl(cloneFilters);
    }
  }

  updateFilters(newFilters: Filters): void {
    if (this.filters().hasChanges(newFilters)) {
      this.filters.set(newFilters);
    }
  }

  getQueryParams(filters: Filters): FiltersQueryParams {
    return {
      date: filters.date.toUTC().toISO(),
      tf: filters.timeframe,
      place: filters.placeId > 0 ? filters.placeId : null,
      panel: this.filterPanelOpened() ? "opened" : "closed",
    };
  }

  async updateFiltersInUrl(
    filters: Filters,
    filterPanelOpened: boolean = null
  ): Promise<void> {
    let panelOpened = this.filterPanelOpened();
    if (filterPanelOpened !== null) {
      panelOpened = filterPanelOpened;
    }
    await this.router.navigate([], {
      replaceUrl: true,
      queryParams: {
        date: filters.date.toUTC().toISO(),
        tf: filters.timeframe,
        place: filters.placeId !== 0 ? filters.placeId : null,
        panel: panelOpened ? "opened" : "closed",
      },
      queryParamsHandling: "merge",
    });
  }

  public clear() {
    // reset local values

    // reset states
    this.filterPanelOpened.set(false);
    this.filters.set(
      new Filters(DateTime.local().toUTC(), Timeframe.Month, null)
    );
  }

  // ---- PRIVATE METHODS

  private _getRoundedDate(date: DateTime) {
    switch (this.filters().timeframe) {
      case Timeframe.Hour:
        return date.endOf("hour");
      case Timeframe.Day:
        return date.endOf("day");
      case Timeframe.Week:
        return date.endOf("week");
      case Timeframe.Month:
        return date.endOf("month");
      case Timeframe.Year:
        return date.endOf("year");
    }
  }
}
